import { Stack, Text, Modal, Button, FileButton, Alert, Group, Loader } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { IconCheck, IconFileText, IconX } from '@tabler/icons-react';
import cx from 'clsx';
import { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { SvgCreate } from '@/components/Common/icon';
import { importFAQTree } from '@mygpt/app';
import { showNotification } from '@mantine/notifications';

interface ImportModalProps {
  opened: boolean;
  onClose: () => void;
  datasetId: string | undefined;
}

export const ImportModal = ({ opened, onClose, datasetId }: ImportModalProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { hovered, ref } = useHover();
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  // 清理进度定时器
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, []);

  // 模拟进度更新 - 针对1分钟的接口调用优化
  const startProgress = () => {
    setProgress(0);
    let currentProgress = 0;
    const stopPoint = Math.random() * 10 + 85; // 随机停在85-95之间，给真实完成留余地

    const getProgressIncrement = (current: number) => {
      // 提升50%速度：增加每次的进度增量
      if (current < 30) {
        return Math.random() * 6 + 3; // 前期每次增加3-9%（原来2-6%）
      } else if (current < 60) {
        return Math.random() * 3 + 1.5; // 中期每次增加1.5-4.5%（原来1-3%）
      } else {
        return Math.random() * 1.5 + 0.75; // 后期每次增加0.75-2.25%（原来0.5-1.5%）
      }
    };

    progressInterval.current = setInterval(() => {
      const increment = getProgressIncrement(currentProgress);
      currentProgress += increment;

      if (currentProgress >= stopPoint) {
        currentProgress = Math.round(stopPoint); // 保持在随机停止点，等待实际请求完成
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
        }
      }
      setProgress(Math.round(currentProgress));
    }, 1000); // 每1秒更新一次，比原来的1.5秒快50%
  };

  const stopProgress = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
    setProgress(100);
    // 延迟重置进度
    setTimeout(() => {
      setProgress(0);
    }, 1000);
  };

  const uploadFile = async () => {
    if (!file || !datasetId) {
      console.warn('file or datasetId is null');
      return;
    }

    setLoading(true);
    startProgress();
    try {
      const {
        data: { success_count, fail_count, total_faqs },
      } = await importFAQTree({ datasetId, file });
      stopProgress();
      // 提示导入成功
      showNotification({
        message: t('faqs.action.import_success', {
          success: `${success_count}/${total_faqs}`,
          fail: `${fail_count}/${total_faqs}`,
        }),
        color: 'green.5',
        icon: <IconCheck />,
      });

      setFile(null);
      onClose();
    } catch (err) {
      stopProgress();
      console.error('导入FAQ树文件失败', err);
      showNotification({
        message: t('faqs.action.import_failed'),
        color: 'red.5',
        icon: <IconX />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('faqs.import.batch_import.batch_import_faq_tree')}
      size="lg"
      centered
      padding="xl"
    >
      <Stack gap={24}>
        <Text size="sm" c="gray.7">
          {t('faqs.import.batch_import.batch_import_faq_tree_description')}
        </Text>
        <Alert variant="light" color="yellow" title={t('faqs.import.batch_import.batch_import_faq_tree_warning_title')}>
          {t('faqs.import.batch_import.batch_import_faq_tree_warning')}
        </Alert>
        <div className="flex items-center justify-end">
          <a
            href={`${import.meta.env.VITE_API_BASE_URL}/datasets/${datasetId}/faqs/download-faq-tree-import-template`}
            className="hover:underline text-sm font-light text-gray-500"
          >
            <Trans
              i18nKey="faqs.import.sheet.download_template"
              components={{
                span: <span className="text-violet-500" />,
              }}
            />
          </a>
        </div>
        <FileButton onChange={setFile} accept=".xlsx,.xls,.md,.docx,.doc,.json,.csv" disabled={loading}>
          {(props) => (
            <div
              {...props}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-violet-400 transition-colors"
              ref={ref}
            >
              {file ? (
                <Stack gap="sm" align="center">
                  <IconFileText size={40} className="text-violet-500" />
                  <Text c="violet.5" fw={500}>
                    {file.name}
                  </Text>
                  <Text size="xs" c="gray.5">
                    {t('faqs.import.batch_import.choose_file')}
                  </Text>
                </Stack>
              ) : (
                <Stack gap="sm" align="center">
                  <span className={cx('flex justify-center', hovered ? 'text-violet-400' : 'text-violet-500')}>
                    <SvgCreate />
                  </span>
                  <Text size="lg" c="violet.5" fw={500}>
                    {t('faqs.import.batch_import.choose_file')}
                  </Text>
                  <Text size="sm" c="gray.5">
                    {t('faqs.import.batch_import.description')}
                  </Text>
                </Stack>
              )}
            </div>
          )}
        </FileButton>

        <Button
          size="md"
          onClick={uploadFile}
          disabled={!file}
          fullWidth
          loading={loading}
          loaderProps={{
            children: (
              <Group>
                <Loader size="xs" color="white" />
                <Text size="xs">{progress}%</Text>
              </Group>
            ),
          }}
        >
          {t('faqs.import.batch_import.upload_file')}
        </Button>
      </Stack>
    </Modal>
  );
};
